/**
 * Demand against capacity, as one bar — and the one thing it must make obvious is HEADROOM.
 *
 * ⚠️ IT IS SCALED TO CAPACITY, NOT TO DEMAND, and that is the whole change (Theresa, Sep 25 2026).
 * The previous bar was a 100% share-of-demand bar: it always filled the width, so it said "TA
 * spends its whole week on open roles" — true, and it made the question people came for
 * unanswerable. "Not easy to see how much headroom there is OR if there is none" was the verdict,
 * and a share bar structurally cannot answer it, because capacity is not on it.
 *
 * So the width is whichever is larger, demand or capacity:
 *
 *   headroom   the tiers fill part of the bar and the REST IS VISIBLY EMPTY, labelled in hours
 *   exact fit  the tiers reach the capacity marker and there is nothing after it
 *   over       the tiers run PAST the marker, and everything beyond it is hatched as the overflow
 *
 * One reading rule, both directions: the marker is the team's week, colour before it is committed
 * work, and whatever sits between the work and the marker is time that could take more.
 */
import { RATINGS, type EffortModel } from '../../../types/rolePrioritisation';
import { hoursLabel, tierHours, tierLoad, type CompanyStats } from '../../../utils/rolePrioritisation';
import { RATING_BG } from './prioritisationUi';

/** A sliver still has to be clickable-looking and countable; below this a tier reads as absent. */
const MIN_SEGMENT_PCT = 1.2;

export function CapacityBar({ stats, model }: { stats: CompanyStats; model: EffortModel }) {
  const demand = stats.demand;
  const capacity = stats.committed;
  const scale = Math.max(demand, capacity, 0.001);
  const over = demand - capacity;
  const headroom = Math.max(0, -over);

  const pct = (h: number) => (h / scale) * 100;
  const capacityPct = pct(capacity);

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="text-sm text-asphalt">
          <b className="font-semibold text-black">{hoursLabel(demand)} h</b> of work on{' '}
          <b className="font-semibold text-black">{hoursLabel(capacity)} h</b> of team time
        </span>
        <span
          data-testid="capacity-verdict"
          className={`text-sm font-semibold ${over > 0.05 ? 'text-red' : 'text-[#0F6B34]'}`}
        >
          {over > 0.05
            ? `Over by ${hoursLabel(over)} h`
            : headroom > 0.05
              ? `${hoursLabel(headroom)} h of headroom`
              : 'Exactly at capacity'}
        </span>
      </div>

      <div
        className="relative mt-2 flex h-8 w-full overflow-hidden rounded-md bg-cotton"
        role="img"
        aria-label={`${hoursLabel(demand)} hours of demand against ${hoursLabel(capacity)} hours of committed capacity${
          over > 0.05
            ? `, over by ${hoursLabel(over)} hours`
            : headroom > 0.05
              ? `, ${hoursLabel(headroom)} hours of headroom`
              : ''
        }`}
      >
        {RATINGS.map((r) => {
          const hours = tierLoad(model, stats.portfolio, r);
          if (hours <= 0) return null;
          return (
            <div
              key={r}
              data-testid={`capacity-seg-${r}`}
              className={RATING_BG[r]}
              style={{ width: `${Math.max(pct(hours), MIN_SEGMENT_PCT)}%` }}
              title={`${r}: ${hoursLabel(hours)} h`}
            />
          );
        })}

        {/*
          ⚠️ THE OVERFLOW IS HATCHED, not another colour. Red already means priority C on this tab —
          the ratings own the green-to-red ramp and a red "too much" band beside a red C segment is
          two colour systems in one bar. A stripe reads as "past the line" without claiming a rating.
        */}
        {over > 0.05 ? (
          <div
            data-testid="capacity-overflow"
            className="absolute inset-y-0 border-l-2 border-solid border-red"
            style={{
              left: `${capacityPct}%`,
              width: `${100 - capacityPct}%`,
              backgroundImage:
                'repeating-linear-gradient(135deg, rgba(231,29,63,0.22) 0 6px, transparent 6px 12px)',
            }}
            title={`Over capacity by ${hoursLabel(over)} h`}
          />
        ) : null}
      </div>

      {/*
        The headroom band is labelled UNDER the bar rather than inside it: at real widths a small
        headroom leaves no room for text, and a label that vanishes exactly when the number gets
        interesting is the wrong way round.
      */}
      <div className="mt-1.5 flex items-start justify-between gap-4 text-[11px] leading-4 text-steel">
        <span>
          {RATINGS.filter((r) => tierLoad(model, stats.portfolio, r) > 0)
            .map(
              (r) =>
                `${r} ${stats.portfolio[r]} × ${hoursLabel(tierHours(model, r))} h = ${hoursLabel(
                  tierLoad(model, stats.portfolio, r)
                )} h`
            )
            .join('  ·  ')}
        </span>
        <span className="shrink-0 text-right">
          {over > 0.05
            ? 'striped = beyond the team’s week'
            : headroom > 0.05
              ? 'empty = headroom'
              : 'no headroom left'}
        </span>
      </div>
    </div>
  );
}

/**
 * How much of the agreed minimum each role actually gets.
 *
 * ⚠️ This replaces "Model coverage 118% · Overcommitted", which was backwards for a reader: the
 * figure went UP as things got worse, and it was named after a thing stakeholders have never heard
 * of. The same arithmetic inverted reads the right way round — below 1.0 is below the promise.
 */
export function ServiceLevelLine({ stats }: { stats: CompanyStats }) {
  const uplift = stats.demand > 0.001 ? stats.committed / stats.demand : 1;
  const short = uplift < 0.995;

  return (
    <p data-testid="service-level" className="mt-3 text-sm leading-5 text-asphalt">
      Every open role is getting{' '}
      <b className={`font-semibold ${short ? 'text-red' : 'text-[#0F6B34]'}`}>
        ×{uplift.toFixed(2)}
      </b>{' '}
      of its agreed minimum
      {short
        ? ' — the portfolio is asking for more than the week holds, so every role is served below the service level.'
        : uplift > 1.005
          ? ' — there is time above the minimum, and section 03 shows which cluster it sits in.'
          : ' — exactly what was agreed, with nothing spare.'}
    </p>
  );
}
